<template>
  <div v-if="fieldValidation && fieldValidation.value" class="space-x-2">
    <template v-if="fieldValidation.match === 'match' || wasApplied">
      <span class="text-xs text-green-700"
        ><WandSparklesIcon class="inline-icon" />
        {{ t(wasApplied ? 'extracted' : fieldValidation.match) }}
      </span>
      <button v-if="wasApplied" class="text-xs text-obsidian-600" @click="revertValue">
        <Undo2Icon class="inline-block h-4 w-4 align-top" /> {{ t('undo') }}
      </button>
    </template>
    <template
      v-else-if="fieldValidation.match === 'correction' || fieldValidation.match === 'extracted'"
    >
      <WandSparklesIcon class="inline-block h-4 w-4 text-blue-600" />
      <button
        class="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800"
        @click="applyValue(parsedValue)"
      >
        <slot name="displayValue" :value="parsedValue">
          {{ parsedValue }}
        </slot>
      </button>
    </template>
    <template v-else-if="fieldValidation.match === 'mismatch'">
      <WandSparklesIcon class="inline-block h-4 w-4 text-bittersweet-600" />
      <button
        class="rounded-full bg-bittersweet-100 px-2 py-1 text-xs font-semibold text-bittersweet-800"
        @click="applyValue(parsedValue)"
      >
        <slot name="displayValue" :value="parsedValue">
          {{ parsedValue }}
        </slot>
      </button>
    </template>
    <template v-if="!wasApplied">
      <button
        v-for="candidate in fieldValidation?.candidates"
        :key="candidate"
        class="inline-block whitespace-nowrap rounded-full bg-hazel-200 px-2 py-1 text-xs font-semibold text-obsidian-800"
        @click="applyValue(props.parseValue(candidate))"
      >
        <slot name="displayValue" :value="props.parseValue(candidate)">
          {{ props.parseValue(candidate) }}
        </slot>
      </button>
    </template>
  </div>
</template>
<script setup lang="ts">
import { useAiValidation } from '@/hooks/use-ai-validation';
import { Undo2Icon, WandSparklesIcon } from 'lucide-vue-next';
import { useFormContext } from 'vee-validate';
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';

const oldValue = ref<any>(null);
const wasApplied = ref<boolean>(false);
const wasAppliedInitially = ref<boolean>(false);

const props = withDefaults(
  defineProps<{
    fieldName: string;
    parseValue?: (value: string) => any;
  }>(),
  {
    parseValue: (value: string) => value,
  },
);

defineSlots<{
  displayValue: (props: { value: any }) => any;
}>();

const fieldValidation = useAiValidation(props.fieldName);

const form = useFormContext();
const { t } = useI18n();
const parsedValue = computed(
  () => fieldValidation.value?.value && props.parseValue(fieldValidation.value.value),
);

const applyValue = (value: any) => {
  oldValue.value = form.values[props.fieldName];
  form.setTouched({ [props.fieldName]: true });
  form.setFieldValue(props.fieldName, value);
  wasApplied.value = true;
};

const revertValue = () => {
  form.setFieldValue(props.fieldName, oldValue.value);
  wasApplied.value = false;
};

watchEffect(() => {
  if (
    fieldValidation.value?.match === 'extracted' &&
    fieldValidation.value?.value &&
    !wasAppliedInitially.value
  ) {
    wasAppliedInitially.value = true;
    applyValue(parsedValue.value);
  }
});
</script>
<i18n lang="json">
{
  "en": {
    "match": "Value matches",
    "extracted": "Value extracted",
    "undo": "Undo"
  },
  "de": {
    "match": "Wert stimmt überein",
    "extracted": "Wert ausgelesen",
    "undo": "Rückgängig"
  },
  "es": {
    "match": "El valor coincide",
    "extracted": "Valor extraído",
    "undo": "Deshacer"
  }
}
</i18n>
